<template>
    <page-content v-if="searchResultsLoaded">
        <template v-slot:foundProducts>
            {{ 'There were' | translation }}
            <span class="font-weight-bold">
                {{ parseInt(searchResults.numFound) }}
                {{ 'Products' | translation }}
            </span>
            {{ 'found' | translation }}
        </template>
        <template v-slot:sortingSelector>
            <sorting-selector class="hidden-sm-and-down text-xs-right" />
        </template>
        <template v-slot:categoryTextTop>
          <div v-if="categoryTextTop"  v-html="categoryTextTop" class="category-text-top" />
        </template>
        <template v-slot:categoryTextBottom>
          <div v-if="categoryTextBottom" v-html="categoryTextBottom" class="category-text-bottom" />
        </template>
        <template v-slot:filterButton>
            <search-filter-btn @toggle-drawer="toggleDrawer" />
        </template>
        <template v-slot:desktopFilters>
            <desktop-filters v-if="$vuetify.breakpoint.mdAndUp" :search-results="searchResults" />
        </template>
        <template v-slot:mobileFilters>
            <mobile-filters v-if="$vuetify.breakpoint.smAndDown" :search-results="searchResults" v-model="drawer" />
        </template>
        <template v-slot:searchResults>
            <v-layout row wrap>
                <v-flex
                    :class="gridClasses"
                    v-for="searchResult in searchResults.docs"
                    :key="`search-item-${searchResult.id}`"
                >
                    <product-thumbnail :product="searchResult" target="Search Results"></product-thumbnail>
                </v-flex>
            </v-layout>
        </template>
        <template v-slot:pagination>
            <v-pagination
                v-if="$vuetify.breakpoint.smAndUp && totalPages > 1"
                v-model="currentPage"
                :length="totalPages"
                :total-visible="9"
                @input="goToPage"
            />

            <v-layout v-else-if="totalPages > 1" justify-center>
                <v-flex shrink>
                    <v-btn @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
                        <v-icon>keyboard_arrow_left</v-icon>
                        {{ 'Previous' | translation }}
                    </v-btn>
                </v-flex>
                <v-flex shrink>
                    <v-btn @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
                        {{ 'Next' | translation }}
                        <v-icon>keyboard_arrow_right</v-icon>
                    </v-btn>
                </v-flex>
            </v-layout>
        </template>
    </page-content>
</template>

<style>
.search-filters {
    background: #fafafa !important;
    color: #707070 !important;
}
.search-filters .theme--light.v-expansion-panel .v-expansion-panel__container,
.search-filters .v-list__tile__title,
.search-filters a,
.search-filters .theme--light.v-sheet,
.search-filters p {
    color: #707070;
}
.search-filters-title {
    background: #e6e6e6;
    border: 1px solid #949494;
}
.search-filters .v-expansion-panel__header {
    padding: 6px !important;
}
.search-filters .v-expansion-panel__body {
    background: white;
    box-shadow: 0 -2px 2px #00000026;
}
</style>

<script>
import Vue from 'vue';
import { createNamespacedHelpers, mapGetters, mapActions } from 'vuex';
import SortingSelector from './SortingSelector';

const moduleName = 'SearchResults';
const searchFiltersModuleName = 'SearchFilters';
const { mapState } = createNamespacedHelpers(moduleName);

let timestamp;

export default {
    name: moduleName,
    components: {
        SortingSelector,
    },
    data() {
        return {
            currentPage:
                this.$router.currentRoute.params.page !== undefined
                    ? parseInt(this.$router.currentRoute.params.page)
                    : 1,
            pagesLimit: this.$store.getters[moduleName + '/searchConfig']('searchPagesAmount'),
            drawer: false,
            metaTitle: '',
        };
    },
    computed: {
        ...mapState([
            'products',
            'lastSearchParams',
            'totalPages',
            'searchResultsLoaded',
            'pageSize',
            'showSearchPlaceholder',
            'gridClasses',
        ]),
        ...mapGetters(searchFiltersModuleName, [
            'getCategoryById',
            'categoryParents',
            'categoriesTree',
            'getAttributeByIndexname',
        ]),
        searchResults() {
            let result = {
                docs: [],
                numFound: 0,
                totalPages: 0,
                facets: [],
                currentCategory: null,
                parentsList: [],
                categories: [],
                totalProducts: 0,
                categoriesFacet: {},
            };

            if (this.products && this.products.total > 0) {
                if (this.lastSearchParams.category && this.lastSearchParams.category !== null) {
                    result.currentCategory = this.getCategoryById(this.lastSearchParams.category);
                    result.parentsList = this.categoryParents(result.currentCategory, []);
                }

                result.categoriesFacet = Object.assign({}, this.products.facets.categories);
                let categoryIds = Object.keys(result.categoriesFacet).filter(id => result.categoriesFacet[id] > 0);
                if (categoryIds.length > 0) {
                    result.categories = this.sortCategories(
                        this.categoriesTree(categoryIds, result.currentCategory ? result.currentCategory.value : null),
                        result.categoriesFacet,
                    );
                }

                result.docs = this.products.data;
                result.numFound = this.products.total;
                result.totalPages = this.totalPages;
                result.facets = this.products.facets;
                result.totalProducts = this.products.total;
            } else if (this.products) {
                result.totalProducts = 0;
            }
            return result;
        },
        getMetaTitle() {
            if (this.searchResults.currentCategory) {
                return this.searchResults.currentCategory.meta_title
                    ? this.searchResults.currentCategory.meta_title
                    : this.searchResults.currentCategory.label;
            } else {
                const keys = new Set();
                if (this.$router.currentRoute.params.query) {
                    keys.add(this.$router.currentRoute.params.query);
                }
                if (this.$router.currentRoute.params.attributes) {
                    Object.values(this.$router.currentRoute.params.attributes).forEach(searchAttributeValue => {
                        searchAttributeValue.forEach(key => keys.add(key));
                    });
                }
                return this.$language
                    .translate('Suchergebnis auf [project-url] für: [keywords]')
                    .replace(['[project-url]'], window.location.host)
                    .replace(['[keywords]'], [...keys].join(' '));
            }
        },
        getMetaDescription() {
            if (this.searchResults.currentCategory) {
                return this.searchResults.currentCategory.meta_description;
            } else {
                return this.metaDescription;
            }
        },
        categoryTextTop() {
            if (this.searchResults.currentCategory) {
                return this.searchResults.currentCategory.category_text_top;
            }
            return false;
        },
        categoryTextBottom() {
            if (this.searchResults.currentCategory) {
                return this.searchResults.currentCategory.category_text_bottom;
            }
            return false;
        },
        country() {
            return this.$country.getCountry();
        },
        currency() {
            return this.$currency.getSelected();
        },
    },
    methods: {
        ...mapActions(moduleName, ['loadResults']),
        ...mapGetters(['searchConfig']),
        ...mapActions(searchFiltersModuleName, ['loadFilters']),
        goToPage: function(value) {
            this.$router.push({
                path: this.$search.getSearchRouteParams(
                    {
                        page: parseInt(value),
                    },
                    true,
                    true,
                ),
            });
        },
        toggleDrawer() {
            this.drawer = !this.drawer;
        },
        sortCategories: function(categories, categoriesFacet) {
            const result = [];
            categories
                .sort((a, b) => {
                    return categoriesFacet[b.value] - categoriesFacet[a.value];
                })
                .forEach(category => {
                    if (category.hasOwnProperty('childrenData')) {
                        category.childrenData = this.sortCategories(category.childrenData, categoriesFacet);
                    }
                    result.push(category);
                });
            return result;
        },
    },
    watch: {
        currency() {
            Vue.$store.dispatch('SearchResults/loadResults', {
                ...this.$route.params,
            });
        },
        country() {
            Vue.$store.dispatch('SearchResults/loadResults', {
                ...this.$route.params,
            });
        },
        lastSearchParams(lastSearchParams) {
            let currentPage = lastSearchParams.page !== undefined ? parseInt(lastSearchParams.page) : 1;
            if (currentPage !== this.currentPage) {
                this.currentPage = currentPage;
            }
        },
    },
    async getOwnLayout(route, from) {
        const localTimestamp = Date.now();
        timestamp = localTimestamp;

        if (!Vue.$search.silent) {
            if (Vue.$store.state.SearchResults.allowSwitchLayout === true) {
                if (Vue.$store.state.SearchResults.searchResultsLoaded === false) {
                    Vue.$store.dispatch('PageContent/switchTemplate', 'search-results-placeholder');
                }
            }

            const response = {
                layout: null,
                template: 'search-results-template',
            };
            const freezeLayoutSwitchCallback = new Promise(resolve => {
                const interval = setInterval(() => {
                    if (Vue.$store.state.SearchResults.allowSwitchLayout === true) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 1000);
            });

            await Vue.$store
                .dispatch('SearchResults/loadResults', {
                    ...route.params,
                    deferredCommit: true,
                })
                .then(result => {
                    let layout = null;
                    let template = 'search-results-template';
                    if (result.hasOwnProperty('layout') && typeof result.layout === 'string' && result.layout.length) {
                        layout = result.layout;
                    }
                    if (
                        result.hasOwnProperty('template') &&
                        typeof result.template === 'string' &&
                        result.template.length
                    ) {
                        template = result.template;
                    }

                    response.layout = layout;
                    response.template = template;
                    if (Vue.$store.state.SearchResults.allowSwitchLayout === false) {
                        response.freezeLayoutSwitch = freezeLayoutSwitchCallback;
                    }

                    if (typeof result.resolveDeferredCommit === 'function') {
                        let setSearchResultsCallback = () => {
                            result.resolveDeferredCommit();

                            Vue.$router.afterHooks.forEach((hook, key) => {
                                if (timestamp === localTimestamp && hook === setSearchResultsCallback) {
                                    Vue.$router.afterHooks.splice(key, 1);
                                }
                            });
                        };
                        if (from && route !== from) {
                            Vue.$router.afterEach(setSearchResultsCallback);
                        } else {
                            setSearchResultsCallback();
                        }
                    }
                });

            return response;
        }
    },
    onLayoutReady() {
        if (Vue.$store.state.PageContent.page && Vue.$store.state.PageContent.page.hasOwnProperty('attributes')) {
            this.metaDescription = Vue.$store.state.PageContent.page.attributes.page_description;
        }
        Vue.$store.commit('SearchResults/SET_SEARCH_RESULTS_LOADED', true);
    },
    metaInfo() {
        return {
            title: this.getMetaTitle,
            meta: [
                {
                    vmid: 'description',
                    name: 'description',
                    content: this.getMetaDescription,
                },
            ],
            __dangerouslyDisableSanitizersByTagID: {
                description: ['content'],
            },
        };
    },
};
</script>
